import CleanInvitationDownload from "../CleanInvitationDownload";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { auth } from "../../firebase";
import { callRsvpCommerce } from "../../Utils/rsvpCommerceApi";
import "./CommercePurchaseReturn.css";

const POLL_DELAY_MS = 1200;
const MAX_STATUS_CHECKS = 50;
const TERMINAL_FAILURES = new Set(["HELD_REVIEW", "REFUND_REVIEW"]);

const returnedOfferId = () => new URLSearchParams(window.location.search).get("commerce_offer") || "";

const clearReturnedOffer = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete("commerce_offer");
  window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
};

const packageLabel = (fulfilledPurchase) => {
  const name = String(
    fulfilledPurchase?.packageSnapshot?.customerFacingName
    || fulfilledPurchase?.packageSnapshot?.packageId
    || ""
  ).trim();
  const upToRecords = Number(fulfilledPurchase?.tierSnapshot?.upToRecords || 0);
  return upToRecords > 0 ? `${name} — עד ${upToRecords.toLocaleString("he-IL")} רשומות` : name;
};

const CommercePurchaseReturn = ({ eventId, onFulfilled }) => {
  const offerId = useMemo(returnedOfferId, []);
  const [view, setView] = useState(offerId ? "checking" : "idle");
  const [fulfilledPurchase, setFulfilledPurchase] = useState(null);
  const [error, setError] = useState("");
  const timerRef = useRef(null);

  const ownerToken = useCallback(async () => {
    const idToken = await auth.currentUser?.getIdToken();
    if (!idToken) throw new Error("RSVP_COMMERCE_AUTH_REQUIRED");
    return idToken;
  }, []);

  const checkStatus = useCallback(async (attempt = 0) => {
    try {
      const result = await callRsvpCommerce({
        idToken: await ownerToken(),
        eventId,
        action: "getPurchaseStatus",
        payload: { offerId },
      });
      if (result.status === "FULFILLED" && result.fulfilledPurchase) {
        await onFulfilled?.(result);
        setFulfilledPurchase(result.fulfilledPurchase);
        setView("success");
        return;
      }
      if (TERMINAL_FAILURES.has(result.status)) {
        setError("התשלום נקלט, אך שיוך החבילה דורש בדיקה. האירוע הקיים נשאר ללא שינוי.");
        setView("error");
        return;
      }
      if (attempt + 1 >= MAX_STATUS_CHECKS) {
        setError("השדרוג עדיין מתעדכן. אפשר לבדוק שוב בלי לבצע רכישה נוספת.");
        setView("error");
        return;
      }
      timerRef.current = window.setTimeout(() => checkStatus(attempt + 1), POLL_DELAY_MS);
    } catch (_) {
      if (attempt + 1 >= MAX_STATUS_CHECKS) {
        setError("לא הצלחנו לאמת את השדרוג כרגע. אפשר לבדוק שוב בלי לבצע רכישה נוספת.");
        setView("error");
        return;
      }
      timerRef.current = window.setTimeout(() => checkStatus(attempt + 1), POLL_DELAY_MS);
    }
  }, [eventId, offerId, onFulfilled, ownerToken]);

  useEffect(() => {
    if (!offerId || !eventId) return undefined;
    void checkStatus();
    return () => window.clearTimeout(timerRef.current);
  }, [checkStatus, eventId, offerId]);

  useEffect(() => {
    if (view === "idle") return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previousOverflow; };
  }, [view]);

  if (view === "idle") return null;

  const continueToManager = () => {
    clearReturnedOffer();
    setView("idle");
  };

  return (
    <div className="commerce-return" role="presentation">
      <section className={`commerce-return__dialog is-${view}`} role="dialog" aria-modal="true" aria-labelledby="commerce-return-title" dir="rtl">
        {view === "checking" && (
          <>
            <span className="commerce-return__spinner" aria-hidden="true" />
            <h1 id="commerce-return-title">מסיימים את השדרוג…</h1>
            <p>התשלום נקלט. אנחנו מחברים את החבילה לאירוע הקיים שלכם.</p>
          </>
        )}
        {view === "success" && (
          <>
            <span className="commerce-return__celebration" aria-hidden="true">🎉</span>
            <h1 id="commerce-return-title">תודה על הרכישה 🎉</h1>
            <h2>{fulfilledPurchase?.optionKind === "CLEAN_INVITATION" ? "ההזמנה ללא סימן מים זמינה להורדה" : "המערכת שודרגה בהצלחה"}</h2>
            <strong className="commerce-return__package">{packageLabel(fulfilledPurchase)}</strong>
            {fulfilledPurchase?.cleanDownloadUrl && <CleanInvitationDownload url={fulfilledPurchase.cleanDownloadUrl} />}
            {fulfilledPurchase?.cleanShareUrl && <a href={fulfilledPurchase.cleanShareUrl} target="_blank" rel="noopener noreferrer">קישור להזמנה ללא סימן מים</a>}
            <button type="button" onClick={continueToManager}>המשך למערכת</button>
          </>
        )}
        {view === "error" && (
          <>
            <span className="commerce-return__notice" aria-hidden="true">!</span>
            <h1 id="commerce-return-title">השדרוג עדיין בבדיקה</h1>
            <p>{error}</p>
            <div className="commerce-return__actions">
              <button type="button" onClick={() => { setError(""); setView("checking"); void checkStatus(); }}>בדיקה חוזרת</button>
              <button type="button" className="is-secondary" onClick={continueToManager}>המשך למערכת</button>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export { clearReturnedOffer, packageLabel, returnedOfferId };
export default CommercePurchaseReturn;
